import request from '@/Network/Requests/request'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import Service from '@/Network/Services/Service'
import type {
  ApproveVersionPayload,
  BulkApprovePayload,
  BulkDecisionResponse,
  BulkRejectPayload,
  ProfessionalDocumentCatalogResponse,
  ProfessionalDocumentsDetailResponse,
  RejectVersionPayload,
  ReviewQueueParams,
  ReviewQueueResponse,
  ReviewQueueSummaryResponse
} from '@/types/professional-documents.types'

/** Query da fila: só campos preenchidos (o backend aplica os defaults). */
export function buildReviewQueueQuery(params: ReviewQueueParams): Record<string, string | number> {
  const query: Record<string, string | number> = {}
  const search = params.search?.trim()
  if (search) query.search = search
  if (params.tag) query.tag = params.tag
  if (params.status) query.status = params.status
  if (params.typeKey) query.typeKey = params.typeKey
  if (params.page) query.page = params.page
  if (params.limit) query.limit = params.limit
  return query
}

/**
 * Revisão de documentos do médico (medical-docs-qualification, SPEC §6.2 + Adendo A3).
 * Com a flag do backend desligada toda rota responde 404 `PROFESSIONAL_DOCUMENTS_DISABLED`;
 * o service relança e quem chama decide com `isProfessionalDocumentsDisabled`.
 */
export default class ProfessionalDocumentsService extends Service {
  constructor() {
    super()
    this.prefix = '/professional-documents'
  }

  async getCatalog(): Promise<ProfessionalDocumentCatalogResponse> {
    try {
      const { data } = await request.get<ProfessionalDocumentCatalogResponse>(
        this.getCompletePath('/catalog')
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o catálogo de documentos')
    }
  }

  async getReviewQueueSummary(): Promise<ReviewQueueSummaryResponse> {
    try {
      const { data } = await request.get<ReviewQueueSummaryResponse>(
        this.getCompletePath('/review-queue/summary')
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a contagem da revisão de documentos')
    }
  }

  async listReviewQueue(params: ReviewQueueParams): Promise<ReviewQueueResponse> {
    try {
      const { data } = await request.get<ReviewQueueResponse>(
        this.getCompletePath('/review-queue'),
        buildReviewQueueQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a fila de documentos')
    }
  }

  /** `userId` = `User.id` do profissional (Adendo A8), não `Doctor.id`. */
  async getProfessional(userId: number): Promise<ProfessionalDocumentsDetailResponse> {
    try {
      const { data } = await request.get<ProfessionalDocumentsDetailResponse>(
        this.getCompletePath(`/professionals/${userId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os documentos do médico')
    }
  }

  /** PDF com a marca d'água de quem está vendo (D28); a URL do blob nunca vai ao navegador. */
  async getVersionFileBlob(versionId: number): Promise<Blob> {
    try {
      const { data } = await request.get<Blob>(
        this.getCompletePath(`/versions/${versionId}/file`),
        undefined,
        undefined,
        'blob'
      )
      return data
    } catch (e) {
      // O corpo de erro chega dentro de um Blob: sem o parse, a mensagem do backend se perde.
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível carregar o arquivo do documento'
      )
    }
  }

  async approve(versionId: number, payload: ApproveVersionPayload): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/versions/${versionId}/approve`), payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar o documento')
    }
  }

  async reject(versionId: number, payload: RejectVersionPayload): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/versions/${versionId}/reject`), payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível reprovar o documento')
    }
  }

  /** Lote com falha parcial (200): quem não pôde ser decidido vem em `failed`. */
  async bulkApprove(payload: BulkApprovePayload): Promise<BulkDecisionResponse> {
    try {
      const { data } = await request.post<BulkDecisionResponse>(
        this.getCompletePath('/versions/bulk-approve'),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar os documentos selecionados')
    }
  }

  async bulkReject(payload: BulkRejectPayload): Promise<BulkDecisionResponse> {
    try {
      const { data } = await request.post<BulkDecisionResponse>(
        this.getCompletePath('/versions/bulk-reject'),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível reprovar os documentos selecionados')
    }
  }

  async undo(versionId: number): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/versions/${versionId}/undo`), {})
    } catch (e) {
      throw this.handleError(e, 'Não foi possível desfazer a decisão')
    }
  }
}
